import React, { useState, useEffect, useRef } from 'react';
import { navigate } from 'gatsby';
import { Box, TextField, Button, Typography, Paper, Alert } from '@mui/material';
import { WEBHOOK_PATHS } from '../config/webhook-urls';
import { n8nFetch, AUTH_401_EVENT } from '../utils/n8n-fetch';
import { clearSnapshots } from '../utils/tabSnapshot';
import { verifySession, markSessionChecked, SESSION_VERSION_KEY } from '../utils/session-check';
import {
  isAppHost, getNeedsSetup, getSellersMeta, getAllowedSellers,
  getFirmOptions, getFirmName, isExternalFirm, SETTINGS_PAGE_READY,
  DEFAULT_N8N_BASE, N8N_BASE_KEY, isCellBase,
} from '../config/app-host';
import { CLIENT_TOOLS } from '../config/client-tools';

// Всё, что логин кладёт в localStorage. Один список на логаут/истечение/отзыв
// (AuthWrapper + Navigation), чтобы новый ключ нельзя было забыть в одном из мест.
export const AUTH_STORAGE_KEYS = [
  'analytics-auth',
  'analytics-auth-time',
  'analytics-role',
  'analytics-username',
  'analytics-sellers',
  'analytics-tools',
  'analytics-token',
  'analytics-sellers-meta',
  'analytics-needs-setup',
  N8N_BASE_KEY, // база n8n ячейки логина (redirect, PR51); нет ключа = NL
  SESSION_VERSION_KEY,
];

export const clearAuthStorage = () => {
  if (typeof window === 'undefined') return;
  AUTH_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
};

// ---- Scoped-access helpers (Stage D2) --------------------------------------
// analytics-sellers: JSON array of allowed WB user_id, or 'null' / absent = все фирмы.
// analytics-tools:   JSON array of allowed tool-names, or 'null' / absent = все инструменты.
// Existing team logins store null (or nothing) → nothing changes for them.

export const getRole = () =>
  (typeof window !== 'undefined' ? localStorage.getItem('analytics-role') : null);

export const getUsername = () =>
  (typeof window !== 'undefined' ? localStorage.getItem('analytics-username') : null);

// Returns array of allowed tool-names, or null = все инструменты (unscoped / team).
export const getAllowedTools = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('analytics-tools');
    if (!raw || raw === 'null') return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : null;
  } catch {
    return null;
  }
};

// Область видимости и фирмы логина живут в src/config/app-host.js (модуль без React,
// его же гоняет scripts/check-firm-options.mjs). Здесь только реэкспорт, чтобы страницы
// импортировали всё из одного места и не завелось второй версии геттера.
export { getAllowedSellers, getSellersMeta, getFirmOptions, getFirmName, isExternalFirm };

export const canUseTool = (name) => {
  const tools = getAllowedTools();
  if (!tools) return true; // null = все инструменты
  return tools.includes(name);
};

// True when the current login is restricted to a subset of firms (e.g. клиент).
export const isScoped = () => getAllowedSellers() !== null;

// Приоритет лендинга клиента: сначала три исторических (у Казанцевой первый же — `bidder`,
// маршрут не меняется), дальше — порядок реестра client-tools.js. Раньше карта страниц
// была своя (bidder, report, notifications, remarking, settings), и клиент без единого из
// этих пяти слагов попадал на «Доступ запрещён» — теперь лендинг находится для любого
// слага реестра. Побочное следствие: `remarking`/`settings` больше не идут сразу за
// тройкой, их обгоняет `fbs-check` (первый в реестре) — у набора вроде
// ['fbs-check','settings'] лендинг стал /fbs-check вместо /settings. Фирму без ключа
// маркетплейса по-прежнему уводит в настройки ветка needs_setup выше.
// Set: у «Сборки FBS» и «QR поставок» общий слаг, дубли в порядке ничего не решают.
const LANDING_ORDER = [...new Set(['bidder', 'report', 'notifications', ...CLIENT_TOOLS.map((t) => t.slug)])];
export const getClientLanding = () => {
  // Фирма без ключа маркетплейса: данных нет ни на одной странице — ведём в настройки.
  // Пока /settings не влит (PR27), флаг выключен и лендинг остаётся прежним.
  if (SETTINGS_PAGE_READY && getNeedsSetup() && canUseTool('settings')) return '/settings';
  const tools = getAllowedTools() || [];
  for (const slug of LANDING_ORDER) {
    const hit = tools.includes(slug) && CLIENT_TOOLS.find((t) => t.slug === slug);
    if (hit) return hit.to;
  }
  return null;
};

// true после первого коммита любого AuthWrapper: дальше рендеры чисто клиентские.
let hydrated = false;

// Роль из живой (≤30 дней) сохранённой сессии, иначе null. Без побочных эффектов —
// чистку протухшей сессии делает checkSavedAuth в эффекте.
const readSavedRole = () => {
  const time = parseInt(localStorage.getItem('analytics-auth-time'), 10);
  if (localStorage.getItem('analytics-auth') !== 'true' || !(Date.now() - time < 30 * 24 * 3600 * 1000)) return null;
  return localStorage.getItem('analytics-role') || null;
};

const AuthWrapper = ({ children, allowPublic = false, requiredRole = null, tool = null }) => {
  // Access gate. admin — суперпользователь (видит всё). Роль 'client' — deny-by-default:
  // проходит страницу ТОЛЬКО если у неё задан tool из разрешённого списка (analytics-tools),
  // поэтому клиент не попадает на дашборд/AI и прочие страницы без tool (нет утечки данных).
  // Обычные роли (manager/seo/content) по-прежнему проверяются по requiredRole. true = запретить.
  const checkAccessDenied = (role) => {
    if (role === 'admin') return false;
    if (role === 'client') return !(tool && canUseTool(tool));
    if (!requiredRole) return false;
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    return !allowedRoles.includes(role);
  };

  // Первый рендер (гидрация) обязан совпасть с SSR — «Загрузка...». Дальше каждая новая
  // страница (меню, холодная вкладка) берёт сессию сразу: иначе кадр «Загрузка...» на весь
  // экран мигал на каждом переходе, как перезагрузка. Эффект ниже всё равно перепроверит.
  const [init] = useState(() => {
    if (!hydrated) return null;
    if (allowPublic) return { auth: true, role: null };
    const role = readSavedRole();
    return { auth: !!role, role };
  });
  const [isAuthenticated, setIsAuthenticated] = useState(!!init?.auth);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!init);
  const [userRole, setUserRole] = useState(init?.role ?? null);
  const [accessDenied, setAccessDenied] = useState(() => !!init?.role && checkAccessDenied(init.role));

  useEffect(() => {
    hydrated = true;
  }, []);

  useEffect(() => {
    if (allowPublic) {
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }

    // Проверяем сохраненную аутентификацию
    const checkSavedAuth = () => {
      const savedAuth = localStorage.getItem('analytics-auth');
      const savedTime = localStorage.getItem('analytics-auth-time');
      const savedRole = localStorage.getItem('analytics-role');

      if (savedAuth === 'true' && savedTime && savedRole) {
        const authTime = parseInt(savedTime, 10);
        const currentTime = Date.now();
        const hoursSinceAuth = (currentTime - authTime) / (1000 * 60 * 60);

        // Сессия действительна 30 дней
        if (hoursSinceAuth < 24 * 30) {
          setIsAuthenticated(true);
          setUserRole(savedRole);
          setAccessDenied(checkAccessDenied(savedRole));
          setLoading(false);
          return;
        }
        // Сессия истекла
        clearAuthStorage();
      }

      setIsAuthenticated(false);
      setUserRole(null);
      setLoading(false);
    };

    checkSavedAuth();
    // Keep-alive вкладки: скрытые экземпляры AuthWrapper остаются смонтированными,
    // поэтому логин/логаут в активной вкладке сообщаем событием.
    window.addEventListener('auth-changed', checkSavedAuth);

    // Серверная проверка: пароль сменили / сессии отозвали (app_users.session_version)
    // → чистим localStorage и показываем форму логина. Fail-open: если n8n/БД
    // недоступны, сессия живёт. Дёргаем при монтировании и при возврате во вкладку;
    // verifySession сама троттлит (одна проверка на все экземпляры, не чаще раза в минуту).
    let unmounted = false;
    // Экземпляров AuthWrapper смонтировано несколько (keep-alive вкладки): гард по
    // analytics-auth оставляет ровно один проход на событие — остальные выходят сразу,
    // поэтому auth-changed уходит один раз.
    const dropSession = (message) => {
      if (localStorage.getItem('analytics-auth') !== 'true') return;
      clearAuthStorage();
      clearSnapshots();
      Object.keys(localStorage)
        .filter((k) => k.startsWith('site-tabs:'))
        .forEach((k) => localStorage.removeItem(k));
      if (!unmounted) setError(message);
      window.dispatchEvent(new Event('auth-changed'));
    };
    const revokeIfStale = () => {
      if (localStorage.getItem('analytics-auth') !== 'true') return;
      verifySession().then((valid) => {
        if (!valid) dropSession('Сессия завершена: пароль изменён или доступ отозван. Войдите заново.');
      });
    };
    revokeIfStale();

    // Гейт авторизации отверг токен: 401 от любого вебхука данных (auth-вебхуки
    // события не шлют — см. n8n-fetch). Запоздавший 401 по прежней сессии игнорируем,
    // иначе он выкинет пользователя сразу после нового логина.
    const onUnauthorized = (e) => {
      const sent = e && e.detail ? e.detail.token : null;
      if (sent && sent !== localStorage.getItem('analytics-token')) return;
      dropSession('Сессия истекла или доступ отозван. Войдите заново.');
    };
    window.addEventListener(AUTH_401_EVENT, onUnauthorized);
    const onVisible = () => {
      if (document.visibilityState === 'visible') revokeIfStale();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      unmounted = true;
      window.removeEventListener('auth-changed', checkSavedAuth);
      window.removeEventListener(AUTH_401_EVENT, onUnauthorized);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowPublic, requiredRole, tool]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setAccessDenied(false);

    if (!username || !password) {
      setError('Введите логин и пароль');
      return;
    }

    try {
      const login = async (base) => {
        const response = await n8nFetch(base + WEBHOOK_PATHS.AUTH, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: username.trim(),
            password: password
          })
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      };

      // Логин всегда начинается в NL (база по умолчанию, не сессии). Логин другой
      // ячейки получает {redirect:{cell, n8n_base}} без токена — повторяем вход один раз
      // в его ячейке; базу берём только из белого списка (туда уйдёт пароль).
      let base = DEFAULT_N8N_BASE;
      let data = await login(base);
      if (data && data.redirect) {
        const next = data.redirect.n8n_base;
        if (!isCellBase(next) || next === base) throw new Error('bad cell redirect');
        base = next;
        data = await login(base);
      }

      if (data.success && data.role) {
        // app.kluchik.io — клиентский хост. Командные роли (manager/seo/stock/…)
        // сюда не пускаем: ничего не сохраняем и чистим возможную старую сессию.
        // На gatsby.kluchik.io проверки нет — вход как сегодня.
        if (isAppHost() && data.role !== 'client' && data.role !== 'admin') {
          clearAuthStorage();
          window.dispatchEvent(new Event('auth-changed'));
          setError('Этот вход для клиентов. Команда — gatsby.kluchik.io');
          setPassword('');
          return;
        }
        setIsAuthenticated(true);
        setUserRole(data.role);
        // Вход = смена пользователя: снимки вкладок (таблицы с ценами и остатками)
        // прежнего пользователя не должны пережить его. Покрывает и логаут из
        // AuthWrapper, и истечение сессии — там снимок просто доживёт до входа.
        clearSnapshots();
        // База ячейки — до всех остальных ключей: с ней уходят первые же запросы вкладок.
        if (base !== DEFAULT_N8N_BASE) localStorage.setItem(N8N_BASE_KEY, base);
        else localStorage.removeItem(N8N_BASE_KEY);
        localStorage.setItem('analytics-auth', 'true');
        localStorage.setItem('analytics-auth-time', Date.now().toString());
        localStorage.setItem('analytics-role', data.role);
        localStorage.setItem('analytics-username', data.username || username.trim());
        // Scoped access: sellers/tools = null (или отсутствуют у старого воркфлоу) → все.
        localStorage.setItem('analytics-sellers', JSON.stringify(data.sellers ?? null));
        localStorage.setItem('analytics-tools', JSON.stringify(data.tools ?? null));
        if (data.token) localStorage.setItem('analytics-token', data.token);
        else localStorage.removeItem('analytics-token');
        // Фирмы логина с метками и признак «ключей ещё нет» (миграция 116).
        // Старый воркфлоу их не отдаёт → [] и false, поведение как раньше.
        localStorage.setItem(
          'analytics-sellers-meta',
          JSON.stringify(Array.isArray(data.sellers_meta) ? data.sellers_meta : [])
        );
        localStorage.setItem('analytics-needs-setup', data.needs_setup === true ? 'true' : 'false');
        // Поколение сессии: старый воркфлоу / БД до миграции не отдают → 1 (default колонки).
        localStorage.setItem(
          SESSION_VERSION_KEY,
          String(Number.isInteger(data.session_version) && data.session_version > 0 ? data.session_version : 1)
        );
        markSessionChecked();

        // Проверяем доступ к странице (admin — суперпользователь; client — по tool)
        if (checkAccessDenied(data.role)) setAccessDenied(true);

        // Будим скрытые keep-alive вкладки, застрявшие на форме логина
        window.dispatchEvent(new Event('auth-changed'));
      } else {
        setError(data.error || 'Неверный логин или пароль');
        setPassword('');
      }
    } catch (fetchError) {
      console.error('Auth error:', fetchError);
      setError('Ошибка подключения к серверу. Проверьте подключение к интернету.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserRole(null);
    setAccessDenied(false);
    clearAuthStorage();
    Object.keys(localStorage)
      .filter((k) => k.startsWith('site-tabs:'))
      .forEach((k) => localStorage.removeItem(k));
    setUsername('');
    setPassword('');
    window.dispatchEvent(new Event('auth-changed'));
  };

  // Свой pathname запоминаем на маунте: страница монтируется только будучи активной,
  // а дальше может жить скрытой (keep-alive) — window.location тогда чужой.
  const ownPathRef = useRef(
    typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') : null
  );

  // Клиента, попавшего на неразрешённую страницу, уводим на его первый доступный инструмент
  // (вместо экрана «Доступ запрещён» и без монтирования виджетов страницы — нет фетчей/утечки).
  const clientLanding = userRole === 'client' ? getClientLanding() : null;
  useEffect(() => {
    if (!accessDenied || userRole !== 'client' || !clientLanding) return;
    if (typeof window === 'undefined') return;
    const here = window.location.pathname.replace(/\/$/, '');
    // Скрытая keep-alive вкладка не должна дёргать навигацию активной
    if (ownPathRef.current !== null && here !== ownPathRef.current) return;
    if (here !== clientLanding) navigate(clientLanding, { replace: true });
  }, [accessDenied, userRole, clientLanding]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  // Client on a disallowed page → show a redirect placeholder while navigate() runs.
  if (accessDenied && userRole === 'client' && clientLanding) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Перенаправление…</Typography>
      </Box>
    );
  }

  if (accessDenied) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: 2
      }}>
        <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Доступ запрещен
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            У вас нет доступа к этой странице. Требуется роль: <strong>{requiredRole}</strong>
          </Alert>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            Ваша роль: <strong>{userRole}</strong>
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={handleLogout}
          >
            Выйти и войти с другой учетной записью
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex' }}>
        {/* Левая часть — форма */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          backgroundColor: '#ffffff',
        }}>
          <Box sx={{ maxWidth: 400, width: '100%' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Вход в систему
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Введите логин и пароль для доступа
            </Typography>

            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Логин"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{ mb: 2 }}
                autoFocus
                autoComplete="username"
              />

              <TextField
                fullWidth
                type="password"
                label="Пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="current-password"
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={!username || !password}
              >
                Войти
              </Button>
            </form>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Сессия действительна 30 дней
            </Typography>
          </Box>
        </Box>
        {/* Правая часть — логотип */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}>
          <img src="/logo.svg" alt="Siman" style={{ width: '60%', maxWidth: 300, height: 'auto' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {children}
    </Box>
  );
};

export default AuthWrapper; 